import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import type { LegalDoc } from "@/content/legal";
import { useLanguage } from "@/i18n/LanguageContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

export const LegalLayout = ({ doc, testId }: { doc: LegalDoc; testId: string }) => {
  const { t } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [doc.title]);

  return (
    <div className="bg-[#F4F4F2] text-left">
      <Header />
      <main data-testid={testId} className="max-w-[1320px] mx-auto px-6 lg:px-8 pt-[104px] md:pt-[120px] pb-16 md:pb-20">
        <Link to="/" data-testid="legal-back-home-link" className="inline-flex items-center gap-2 text-sm font-medium text-neutral-600 hover:text-[#2E4FE6] transition-colors">
          <ArrowLeft className="w-4 h-4" /> {t.legal.back}
        </Link>

        <header className="mt-8 pb-8 border-b border-neutral-200">
          <div className="text-[#2E4FE6] font-bold text-xs tracking-[0.22em] uppercase mb-4">{doc.eyebrow}</div>
          <h1 data-testid="legal-page-title" className="font-display text-[clamp(1.9rem,4.5vw,3.8rem)] uppercase text-[#111] break-words">
            {doc.title}
          </h1>
          <p className="mt-4 text-xs font-bold tracking-[0.18em] uppercase text-neutral-400" data-testid="legal-last-updated">
            {t.legal.updated}: {doc.updated}
          </p>
          <p className="mt-6 max-w-3xl text-lg text-neutral-600 leading-relaxed">{doc.intro}</p>
        </header>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-4">
            <nav className="lg:sticky lg:top-24 bg-white rounded-lg p-6 border border-neutral-100" data-testid="legal-toc">
              <div className="text-xs font-bold tracking-[0.18em] uppercase text-neutral-400 mb-4">{t.legal.index}</div>
              <ol className="space-y-2">
                {doc.sections.map((s, i) => (
                  <li key={s.id}>
                    <a href={`#${s.id}`} className="flex gap-3 text-sm text-neutral-700 hover:text-[#2E4FE6] transition-colors">
                      <span className="font-bold text-[#F2641E] tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                      {s.heading}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          </aside>

          <article className="lg:col-span-8 max-w-3xl">
            {doc.sections.map((s, i) => (
              <section key={s.id} id={s.id} data-testid={`legal-section-${s.id}`} className="py-7 border-b border-neutral-200 last:border-0">
                <h2 className="font-archivo font-extrabold text-xl md:text-2xl text-[#111]">
                  <span className="text-[#F2641E] mr-3">{String(i + 1).padStart(2, "0")}</span>
                  {s.heading}
                </h2>
                {s.paragraphs.map((p, j) => (
                  <p key={j} className="mt-4 text-base leading-relaxed text-neutral-600">
                    {p}
                  </p>
                ))}
                {s.bullets && (
                  <ul className="mt-4 space-y-2">
                    {s.bullets.map((b, j) => (
                      <li key={j} className="flex gap-3 text-base leading-relaxed text-neutral-600">
                        <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#2E4FE6]" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </article>
        </div>
      </main>
      <Footer />
    </div>
  );
};
